import { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import Header from '@/components/Header';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { Badge } from '@/components/ui/badge';
import { Save, Clock, Calendar, AlertTriangle, Check, X, Search } from 'lucide-react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';

// Reuse the User type from UsersPage
type User = {
  id: string;
  name: string;
  status: 'active' | 'inactive' | 'pending';
  role: 'admin' | 'manager' | 'viewer' | 'disabled' | 'archived';
  organization: string;
  lastActive: string;
  details: {
    email: string;
    phone: string;
    department: string;
  };
  permissions: {
    sites: string[];
    features: string[];
  };
};

// Mock data for sites and features
const availableSites = ['Site A', 'Site B', 'Site C', 'Site D', 'Site E'];
const availableFeatures = ['Monitoring', 'Workflows', 'Devices', 'Analytics', 'Customers'];

// Mock users data (same as in UsersPage)
const users: User[] = [
  {
    id: '1',
    name: 'John Smith',
    status: 'active',
    role: 'admin',
    organization: 'Acme Corporation',
    lastActive: '2025-01-20',
    details: {
      email: 'john.smith@acme.com',
      phone: '+1 (555) 123-4567',
      department: 'IT Security',
    },
    permissions: {
      sites: ['All Sites'],
      features: ['All Features'],
    },
  },
  {
    id: '2',
    name: 'Sarah Johnson',
    status: 'active',
    role: 'manager',
    organization: 'TechStart Solutions',
    lastActive: '2025-01-19',
    details: {
      email: 'sarah@techstart.io',
      phone: '+1 (555) 987-6543',
      department: 'Operations',
    },
    permissions: {
      sites: ['Site A', 'Site B'],
      features: ['Monitoring', 'Analytics'],
    },
  },
  {
    id: '3',
    name: 'Michael Brown',
    status: 'inactive',
    role: 'viewer',
    organization: 'Global Industries Ltd',
    lastActive: '2025-01-15',
    details: {
      email: 'm.brown@globalind.com',
      phone: '+44 20 7123 4567',
      department: 'Security',
    },
    permissions: {
      sites: ['Site C'],
      features: ['Monitoring'],
    },
  },
  {
    id: '4',
    name: 'Emma Wilson',
    status: 'pending',
    role: 'manager',
    organization: 'Local Business Co',
    lastActive: '2025-01-18',
    details: {
      email: 'emma@localbusiness.com.au',
      phone: '+61 3 9876 5432',
      department: 'IT',
    },
    permissions: {
      sites: ['Site D', 'Site E'],
      features: ['Security', 'Analytics'],
    },
  },
];

export function EditUsersPage() {
  const navigate = useNavigate();
  const { userId } = useParams();

  // Find the user if editing, or use default values if adding new user
  const existingUser = userId ? users.find((user) => user.id === userId) : null;

  const [formData, setFormData] = useState<Omit<User, 'id' | 'lastActive'>>({
    name: existingUser?.name || '',
    status: existingUser?.status || 'pending',
    role: existingUser?.role || 'viewer',
    organization: existingUser?.organization || '',
    details: {
      email: existingUser?.details.email || '',
      phone: existingUser?.details.phone || '',
      department: existingUser?.details.department || '',
    },
    permissions: {
      sites: existingUser?.permissions.sites || [],
      features: existingUser?.permissions.features || [],
    },
  });

  const handleInputChange = (field: string, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleDetailsChange = (field: string, value: string) => {
    setFormData((prev) => ({
      ...prev,
      details: { ...prev.details, [field]: value },
    }));
  };

  const handleSiteToggle = (site: string) => {
    setFormData((prev) => {
      const sites = prev.permissions.sites.includes(site)
        ? prev.permissions.sites.filter((s) => s !== site)
        : [...prev.permissions.sites, site];

      return {
        ...prev,
        permissions: {
          ...prev.permissions,
          sites,
        },
      };
    });
  };

  const handleFeatureToggle = (feature: string) => {
    setFormData((prev) => {
      const features = prev.permissions.features.includes(feature)
        ? prev.permissions.features.filter((f) => f !== feature)
        : [...prev.permissions.features, feature];

      return {
        ...prev,
        permissions: {
          ...prev.permissions,
          features,
        },
      };
    });
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    // Here you would typically save the data to your backend
    console.log('Saving user:', formData);

    // Navigate back to users page
    navigate('/users');
  };

  return (
    <>
      <div className="">
        <div className="sticky top-14 flex items-center justify-between border-b bg-[#111] p-2">
          <div>
            <h1 className="flex items-center font-semibold text-white">
              <Link to="/users" className="text-lime">
                Users&nbsp;
              </Link>{' '}
              /&nbsp;{existingUser ? 'USER NAME' : 'Add User'}
            </h1>
          </div>{' '}
          <div className="mr-3 flex justify-end gap-4">
            <Button type="button" variant="outline" onClick={() => navigate('/users')}>
              Cancel
            </Button>
            <Button
              type="submit"
              className="flex items-center gap-2 bg-lime text-black hover:bg-lime/90"
            >
              <Save className="h-4 w-4" /> Save User
            </Button>
          </div>
        </div>

        <Tabs defaultValue="details" className="w-full">
          <TabsList className="w-full flex-1 border-x-0 border-t-0 bg-transparent">
            <TabsTrigger
              value="details"
              className="data-[state=active]:bg-background data-[state=active]:text-lime"
            >
              User Details
            </TabsTrigger>

            <TabsTrigger
              value="alerts"
              className="data-[state=active]:bg-background data-[state=active]:text-lime"
            >
              Alerts
            </TabsTrigger>
            <TabsTrigger
              value="activity"
              className="data-[state=active]:bg-background data-[state=active]:text-lime"
            >
              Activity Log
            </TabsTrigger>
          </TabsList>

          <div className="p-6">
            <TabsContent value="details">
              <div className="mx-auto mb-4 flex max-w-[1280px] items-center justify-between rounded-lg border bg-card2 p-4">
                <div className="flex items-center">
                  <img src="https://placehold.co/40x40" className="rounded-md" alt="" />
                  <div className="ml-4">
                    <p className="font-semibold text-white">John Smith</p>
                    <p className="text-sm text-gray-300">johnsmith@EXAMPLEORG.com</p>
                  </div>
                </div>
                <div className="flex flex-col gap-1 text-sm text-gray-300">
               
                </div>
              </div>
              <section className="mx-auto grid max-w-[1280px] grid-cols-[320px_1fr] gap-6">
                <div>
                  <div className="sticky top-36 flex flex-col gap-2 font-semibold text-lime">
                    <a href="#user">User Details</a>
                    <a href="#user">Security</a>
                    <a href="#user">Access</a>
                    <a href="#user">Notifications</a>
                  </div>
                </div>

                <form onSubmit={handleSubmit}>
                  <div className="grid grid-cols-1 gap-6">
                    {/* Basic Information */}
                    <Card className="border-white/10">
                      <CardHeader>
                        <CardTitle className="text-white">Basic Information Test</CardTitle>
                        <CardDescription>User profile and account details</CardDescription>
                      </CardHeader>
                      <CardContent className="grid grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label htmlFor="name" className="text-white">
                            Full Name
                          </Label>
                          <Input
                            id="name"
                            value={formData.name}
                            onChange={(e) => handleInputChange('name', e.target.value)}
                            className="bg-card2"
                          />
                        </div>

                        <div className="space-y-2">
                          <Label htmlFor="email" className="text-white">
                            Email
                          </Label>
                          <Input
                            id="email"
                            type="email"
                            value={formData.details.email}
                            onChange={(e) => handleDetailsChange('email', e.target.value)}
                            className="bg-card2"
                          />
                        </div>

                        <div className="space-y-2">
                          <Label htmlFor="phone" className="text-white">
                            Phone
                          </Label>
                          <Input
                            id="phone"
                            value={formData.details.phone}
                            onChange={(e) => handleDetailsChange('phone', e.target.value)}
                            className="bg-card2"
                          />
                        </div>
                      </CardContent>
                    </Card>

                    {/* Role and Status */}
                    <Card className="border-white/10">
                      <CardHeader>
                        <CardTitle className="text-white">Security</CardTitle>
                        <CardDescription>User role and account status</CardDescription>
                      </CardHeader>
                      <CardContent className="gap-4">
                        {' '}
                        <div>
                          <Label
                            htmlFor="role"
                            className="mb-3 block text-sm font-semibold text-white/70"
                          >
                            Role
                          </Label>
                          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
                            <label
                              className={`flex cursor-pointer flex-col gap-2 rounded-lg border p-4 transition-colors hover:bg-card2 ${
                                formData.role === 'admin'
                                  ? 'border-lime bg-card2'
                                  : 'border-white/10'
                              }`}
                            >
                              <div className="flex items-center gap-2">
                                <input
                                  type="radio"
                                  name="role"
                                  value="admin"
                                  checked={formData.role === 'admin'}
                                  onChange={() => handleInputChange('role', 'admin')}
                                  className="hidden"
                                />
                                <div
                                  className={`h-4 w-4 rounded-full ${formData.role === 'admin' ? 'bg-lime' : 'border border-white/30'}`}
                                ></div>
                                <span className="font-semibold text-white">Admin</span>
                              </div>
                              <p className="text-sm text-white/60">
                                Full access to all features and settings, including user management
                              </p>
                            </label>

                            <label
                              className={`flex cursor-pointer flex-col gap-2 rounded-lg border p-4 transition-colors hover:bg-card2 ${
                                formData.role === 'manager'
                                  ? 'border-lime bg-card2'
                                  : 'border-white/10'
                              }`}
                            >
                              <div className="flex items-center gap-2">
                                <input
                                  type="radio"
                                  name="role"
                                  value="manager"
                                  checked={formData.role === 'manager'}
                                  onChange={() => handleInputChange('role', 'manager')}
                                  className="hidden"
                                />
                                <div
                                  className={`h-4 w-4 rounded-full ${formData.role === 'manager' ? 'bg-lime' : 'border border-white/30'}`}
                                ></div>
                                <span className="font-semibold text-white">Manager</span>
                              </div>
                              <p className="text-sm text-white/60">
                                Can manage alerts and sites, but cannot modify system settings
                              </p>
                            </label>

                            <label
                              className={`flex cursor-pointer flex-col gap-2 rounded-lg border p-4 transition-colors hover:bg-card2 ${
                                formData.role === 'viewer'
                                  ? 'border-lime bg-card2'
                                  : 'border-white/10'
                              }`}
                            >
                              <div className="flex items-center gap-2">
                                <input
                                  type="radio"
                                  name="role"
                                  value="viewer"
                                  checked={formData.role === 'viewer'}
                                  onChange={() => handleInputChange('role', 'viewer')}
                                  className="hidden"
                                />
                                <div
                                  className={`h-4 w-4 rounded-full ${formData.role === 'viewer' ? 'bg-lime' : 'border border-white/30'}`}
                                ></div>
                                <span className="font-semibold text-white">Viewer</span>
                              </div>
                              <p className="text-sm text-white/60">
                                Read-only access to view alerts and monitoring data
                              </p>
                            </label>

                            <label
                              className={`flex cursor-pointer flex-col gap-2 rounded-lg border p-4 transition-colors hover:bg-card2 ${
                                formData.role === 'disabled'
                                  ? 'border-lime bg-card2'
                                  : 'border-white/10'
                              }`}
                            >
                              <div className="flex items-center gap-2">
                                <input
                                  type="radio"
                                  name="role"
                                  value="disabled"
                                  checked={formData.role === 'disabled'}
                                  onChange={() => handleInputChange('role', 'disabled')}
                                  className="hidden"
                                />
                                <div
                                  className={`h-4 w-4 rounded-full ${formData.role === 'disabled' ? 'bg-lime' : 'border border-white/30'}`}
                                ></div>
                                <span className="font-semibold text-white">Disabled</span>
                              </div>
                              <p className="text-sm text-white/60">
                                Account exists but cannot log in or access any features
                              </p>
                            </label>

                            <label
                              className={`flex cursor-pointer flex-col gap-2 rounded-lg border p-4 transition-colors hover:bg-card2 ${
                                formData.role === 'archived'
                                  ? 'border-lime bg-card2'
                                  : 'border-white/10'
                              }`}
                            >
                              <div className="flex items-center gap-2">
                                <input
                                  type="radio"
                                  name="role"
                                  value="archived"
                                  checked={formData.role === 'archived'}
                                  onChange={() => handleInputChange('role', 'archived')}
                                  className="hidden"
                                />
                                <div
                                  className={`h-4 w-4 rounded-full ${formData.role === 'archived' ? 'bg-lime' : 'border border-white/30'}`}
                                ></div>
                                <span className="font-semibold text-white">Archived</span>
                              </div>
                              <p className="text-sm text-white/60">
                                Permanently deactivated account preserved for record-keeping
                              </p>
                            </label>
                          </div>
                        </div>
                        <section className="col-span-2 pt-6">
                          {/* Only show feature access for manager and viewer roles */}
                          {!['admin', 'disabled', 'archived'].includes(formData.role) && (
                            <>
                              <h4 className="mb-3 text-sm font-semibold text-white/60">
                                Feature Access
                              </h4>
                              <p className="mb-4 text-sm text-white/60">
                                Select which features this user can access
                              </p>
                              <div className="mb-8 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
                                {availableFeatures.map((feature) => (
                                  <label
                                    key={feature}
                                    className="flex cursor-pointer items-center gap-3 rounded-lg border border-white/10 p-4 transition-colors hover:bg-card2"
                                    htmlFor={`feature-${feature}`}
                                  >
                                    <Checkbox
                                      id={`feature-${feature}`}
                                      checked={formData.permissions.features.includes(feature)}
                                      onCheckedChange={() => handleFeatureToggle(feature)}
                                    />
                                    <div>
                                      <p className="font-semibold text-white">{feature}</p>
                                    </div>
                                  </label>
                                ))}
                              </div>
                            </>
                          )}
                          <h4 className="mb-3 text-sm font-semibold text-white/60">
                            Security Options
                          </h4>
                          <div className="grid gap-3 sm:grid-cols-3">
                            <label className="flex cursor-pointer flex-col gap-2 rounded-lg border border-white/10 p-4 transition-colors hover:bg-card2">
                              <div className="flex items-center gap-2">
                                <Checkbox id="2fa" />
                                <span className="font-semibold text-white">
                                  Two-Factor Authentication
                                </span>
                              </div>
                              <p className="text-sm text-white/60">
                                Require additional verification when logging in
                              </p>
                            </label>

                            <label className="flex cursor-pointer flex-col gap-2 rounded-lg border border-white/10 p-4 transition-colors hover:bg-card2">
                              <div className="flex items-center gap-2">
                                <Checkbox id="ip-restrict" />
                                <span className="font-semibold text-white">IP Restriction</span>
                              </div>
                              <p className="text-sm text-white/60">
                                Limit access to specific IP addresses
                              </p>
                            </label>

                            <label className="flex cursor-pointer flex-col gap-2 rounded-lg border border-white/10 p-4 transition-colors hover:bg-card2">
                              <div className="flex items-center gap-2">
                                <Checkbox id="auto-logout" />
                                <span className="font-semibold text-white">Auto Logout</span>
                              </div>
                              <p className="text-sm text-white/60">
                                Automatically log out after 30 minutes of inactivity
                              </p>
                            </label>
                          </div>
                        </section>
                      </CardContent>
                    </Card>
                    <Card className="border-white/10">
                      <CardHeader>
                        <CardTitle className="text-white"> Monitoring Teams</CardTitle>
                        <CardDescription>Assign user to monitoring teams</CardDescription>
                      </CardHeader>
                      <CardContent>
                        <div className="space-y-4">
                          <div className="relative">
                            <Search className="absolute left-2 top-2.5 h-4 w-4 text-white/50" />
                            <Input placeholder="Search teams..." className="bg-card2 pl-8" />
                          </div>

                          <div className="grid gap-3 sm:grid-cols-2">
                            {/* Team Card 1 */}
                            <label className="flex cursor-pointer flex-col gap-2 rounded-lg border border-white/10 p-4 transition-colors hover:bg-card2">
                              <div className="flex items-center justify-between">
                                <div className="flex items-center gap-2">
                                  <Checkbox id="team-1" />
                                  <span className="font-semibold text-white">Night Shift Team</span>
                                </div>
                                <Badge className="bg-[#B7D257] text-black">12 Cameras</Badge>
                              </div>
                              <div className="mt-2">
                                <p className="mb-2 text-sm text-white/60">Team Members:</p>
                                <div className="flex flex-wrap gap-1">
                                  <Badge variant="outline" className="bg-card2/50 text-white">
                                    John Smith
                                  </Badge>
                                  <Badge variant="outline" className="bg-card2/50 text-white">
                                    Sarah Johnson
                                  </Badge>
                                  <Badge variant="outline" className="bg-card2/50 text-white">
                                    Mike Lee
                                  </Badge>
                                  <Badge variant="outline" className="bg-card2/50 text-white">
                                    +2 more
                                  </Badge>
                                </div>
                              </div>
                            </label>

                            {/* Team Card 2 */}
                            <label className="flex cursor-pointer flex-col gap-2 rounded-lg border border-white/10 p-4 transition-colors hover:bg-card2">
                              <div className="flex items-center justify-between">
                                <div className="flex items-center gap-2">
                                  <Checkbox id="team-2" />
                                  <span className="font-semibold text-white">Day Shift Team</span>
                                </div>
                                <Badge className="bg-[#B7D257] text-black">8 Cameras</Badge>
                              </div>
                              <div className="mt-2">
                                <p className="mb-2 text-sm text-white/60">Team Members:</p>
                                <div className="flex flex-wrap gap-1">
                                  <Badge variant="outline" className="bg-card2/50 text-white">
                                    Alex Wong
                                  </Badge>
                                  <Badge variant="outline" className="bg-card2/50 text-white">
                                    Emma Davis
                                  </Badge>
                                  <Badge variant="outline" className="bg-card2/50 text-white">
                                    James Wilson
                                  </Badge>
                                </div>
                              </div>
                            </label>

                            {/* Team Card 3 */}
                            <label className="flex cursor-pointer flex-col gap-2 rounded-lg border border-white/10 p-4 transition-colors hover:bg-card2">
                              <div className="flex items-center justify-between">
                                <div className="flex items-center gap-2">
                                  <Checkbox id="team-3" />
                                  <span className="font-semibold text-white">Weekend Team</span>
                                </div>
                                <Badge className="bg-[#B7D257] text-black">6 Cameras</Badge>
                              </div>
                              <div className="mt-2">
                                <p className="mb-2 text-sm text-white/60">Team Members:</p>
                                <div className="flex flex-wrap gap-1">
                                  <Badge variant="outline" className="bg-card2/50 text-white">
                                    David Brown
                                  </Badge>
                                  <Badge variant="outline" className="bg-card2/50 text-white">
                                    Lisa Chen
                                  </Badge>
                                </div>
                              </div>
                            </label>

                            {/* Team Card 4 */}
                            <label className="flex cursor-pointer flex-col gap-2 rounded-lg border border-white/10 p-4 transition-colors hover:bg-card2">
                              <div className="flex items-center justify-between">
                                <div className="flex items-center gap-2">
                                  <Checkbox id="team-4" />
                                  <span className="font-semibold text-white">
                                    Emergency Response
                                  </span>
                                </div>
                                <Badge className="bg-[#B7D257] text-black">15 Cameras</Badge>
                              </div>
                              <div className="mt-2">
                                <p className="mb-2 text-sm text-white/60">Team Members:</p>
                                <div className="flex flex-wrap gap-1">
                                  <Badge variant="outline" className="bg-card2/50 text-white">
                                    Robert Taylor
                                  </Badge>
                                  <Badge variant="outline" className="bg-card2/50 text-white">
                                    Jennifer Moore
                                  </Badge>
                                  <Badge variant="outline" className="bg-card2/50 text-white">
                                    Kevin Zhang
                                  </Badge>
                                  <Badge variant="outline" className="bg-card2/50 text-white">
                                    +4 more
                                  </Badge>
                                </div>
                              </div>
                            </label>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                    <Card className="border-white/10">
                      <CardHeader>
                        <CardTitle className="text-white"> Permissions</CardTitle>
                      </CardHeader>
                      <CardContent>aaaa</CardContent>
                    </Card>

                    {/* Permissions - Sites */}
                    <Card className="border-white/10">
                      <CardHeader>
                        <CardTitle className="text-white"> Access</CardTitle>
                        <CardDescription>Select which sites this user can access</CardDescription>
                      </CardHeader>
                      <CardContent>
                        <label className="flex items-center space-x-2 rounded-lg border border-white/10 p-4">
                          <Checkbox
                            id="site-all"
                            checked={formData.permissions.sites.includes('All Sites')}
                            onCheckedChange={() => handleSiteToggle('All Sites')}
                          />
                          <Label htmlFor="site-all" className="font-semibold text-white">
                            All Customers &amp; Sites
                          </Label>
                        </label>

                        {!formData.permissions.sites.includes('All Sites') && (
                          <>
                            <div className="mt-4 rounded-lg border border-white/10 p-4">
                              <Label htmlFor="site-search" className="text-white">
                                Search Customers &amp; Sites
                              </Label>
                              <Input type="text" id="site-search" />
                            </div>
                            <section className="mt-4">
                              <h3 className="mb-2 font-semibold">Customers &amp; Sites</h3>
                              <div className="space-y-2">
                                <Accordion type="multiple" className="w-full p-2">
                                  {/* Group sites by customer - this is a simplified example */}
                                  {/* In a real app, you would have a proper data structure with customers and their sites */}

                                  <AccordionItem
                                    value="customer-1"
                                    className="mb-2 rounded-md border border-white/10"
                                  >
                                    <div className="flex items-center">
                                      <label className="cursor-pointer border-r p-4 hover:bg-[#1a201f]">
                                        <Checkbox
                                          id="customer-2-check"
                                          onCheckedChange={(checked) => {
                                            console.log('Customer 2 checked:', checked);
                                          }}
                                        />
                                      </label>
                                      <AccordionTrigger className="flex-1 p-4 font-semibold text-lime hover:no-underline">
                                        Wellington Security Ltd
                                      </AccordionTrigger>
                                    </div>
                                    <AccordionContent className="border-t">
                                      {/* Filter sites for this customer */}
                                      {availableSites.map((site) => (
                                        <Label
                                          key={site}
                                          className="flex cursor-pointer items-center gap-3 border-b border-white/10 p-4 last:border-b-0 hover:bg-[#1a201f] hover:underline"
                                          htmlFor={`site-${site}`}
                                        >
                                          <Checkbox
                                            id={`site-${site}`}
                                            checked={formData.permissions.sites.includes(site)}
                                            onCheckedChange={() => handleSiteToggle(site)}
                                          />
                                          <p>
                                            <strong className="text-lime">{site}</strong> - X
                                            Devices
                                          </p>
                                        </Label>
                                      ))}
                                    </AccordionContent>
                                  </AccordionItem>
                                  <AccordionItem
                                    value="customer-2"
                                    className="mb-2 rounded-md border border-white/10"
                                  >
                                    <div className="flex items-center">
                                      <label className="cursor-pointer border-r p-4 hover:bg-[#1a201f]">
                                        <Checkbox
                                          id="customer-2-check"
                                          onCheckedChange={(checked) => {
                                            console.log('Customer 2 checked:', checked);
                                          }}
                                        />
                                      </label>
                                      <AccordionTrigger className="flex-1 p-4 font-semibold text-lime hover:no-underline">
                                        Wellington Security Ltd
                                      </AccordionTrigger>
                                    </div>
                                    <AccordionContent className="border-t">
                                      {/* Filter sites for this customer */}
                                      {availableSites.map((site) => (
                                        <Label
                                          key={site}
                                          className="flex cursor-pointer items-center gap-3 border-b border-white/10 p-4 last:border-b-0 hover:bg-[#1a201f] hover:underline"
                                          htmlFor={`site-${site}`}
                                        >
                                          <Checkbox
                                            id={`site-${site}`}
                                            checked={formData.permissions.sites.includes(site)}
                                            onCheckedChange={() => handleSiteToggle(site)}
                                          />
                                          <p>
                                            <strong className="text-lime">{site}</strong> - X
                                            Devices
                                          </p>
                                        </Label>
                                      ))}
                                    </AccordionContent>
                                  </AccordionItem>
                                </Accordion>
                              </div>
                            </section>
                          </>
                        )}
                      </CardContent>
                    </Card>
                  </div>
                  <div className="mt-6 grid grid-cols-1 gap-6">
                    <Card className="border-white/10">
                      <CardHeader>
                        <CardTitle className="text-white">Alert Settings</CardTitle>
                        <CardDescription>Configure user alert preferences</CardDescription>
                      </CardHeader>
                      <CardContent className="space-y-6">
                        <div>
                          <h3 className="mb-3 text-sm font-semibold text-white/70">Alert Types</h3>
                          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
                            <label className="flex cursor-pointer items-center gap-3 rounded-lg border border-white/10 p-4 transition-colors hover:bg-card2">
                              <Checkbox id="alert-security" defaultChecked />
                              <div>
                                <p className="font-semibold text-white">Security Alerts</p>
                                <p className="text-sm text-white/60">
                                  Intruder and threat notifications
                                </p>
                              </div>
                            </label>
                            <label className="flex cursor-pointer items-center gap-3 rounded-lg border border-white/10 p-4 transition-colors hover:bg-card2">
                              <Checkbox id="alert-system" defaultChecked />
                              <div>
                                <p className="font-semibold text-white">System Notifications</p>
                                <p className="text-sm text-white/60">
                                  Status updates and maintenance
                                </p>
                              </div>
                            </label>
                            <label className="flex cursor-pointer items-center gap-3 rounded-lg border border-white/10 p-4 transition-colors hover:bg-card2">
                              <Checkbox id="alert-updates" />
                              <div>
                                <p className="font-semibold text-white">Product Updates</p>
                                <p className="text-sm text-white/60">
                                  New features and improvements
                                </p>
                              </div>
                            </label>
                          </div>
                        </div>

                        <div className="pt-2">
                          <h3 className="mb-3 text-sm font-semibold text-white/70">
                            Delivery Methods
                          </h3>
                          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
                            <label className="flex cursor-pointer items-center gap-3 rounded-lg border border-white/10 p-4 transition-colors hover:bg-card2">
                              <Checkbox id="delivery-email" defaultChecked />
                              <div>
                                <p className="font-semibold text-white">Email</p>
                                <p className="text-sm text-white/60">
                                  Send to user's email address
                                </p>
                              </div>
                            </label>
                            <label className="flex cursor-pointer items-center gap-3 rounded-lg border border-white/10 p-4 transition-colors hover:bg-card2">
                              <Checkbox id="delivery-sms" />
                              <div>
                                <p className="font-semibold text-white">SMS</p>
                                <p className="text-sm text-white/60">Send to mobile number</p>
                              </div>
                            </label>
                            <label className="flex cursor-pointer items-center gap-3 rounded-lg border border-white/10 p-4 transition-colors hover:bg-card2">
                              <Checkbox id="delivery-push" defaultChecked />
                              <div>
                                <p className="font-semibold text-white">Push Notifications</p>
                                <p className="text-sm text-white/60">
                                  Send to browser or mobile app
                                </p>
                              </div>
                            </label>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  </div>
                </form>
              </section>
            </TabsContent>

            <TabsContent value="alerts">
              <div className="mx-auto max-w-[1280px]">
                <Card className="border-white/10">
                  <CardHeader>
                    <div className="flex items-center justify-between">
                      <div>
                        <CardTitle className="text-white">Resolved Alerts</CardTitle>
                        <CardDescription>Alerts this user has processed</CardDescription>
                      </div>
                      <div className="flex items-center gap-2">
                        <Select defaultValue="all">
                          <SelectTrigger className="w-[180px] bg-card2">
                            <SelectValue placeholder="Filter by type" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="all">All Alerts</SelectItem>
                            <SelectItem value="dismissed">Dismissed</SelectItem>
                            <SelectItem value="escalated">Escalated</SelectItem>
                            <SelectItem value="resolved">Resolved</SelectItem>
                          </SelectContent>
                        </Select>
                        <Button variant="outline" size="sm">
                          Export
                        </Button>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <Table>
                      <TableHeader>
                        <TableRow className="border-white/10 hover:bg-card2/50">
                          <TableHead className="w-[100px]">Status</TableHead>
                          <TableHead>Alert ID</TableHead>
                          <TableHead>Customer</TableHead>
                          <TableHead>Site</TableHead>
                          <TableHead>Type</TableHead>
                          <TableHead>Time</TableHead>
                          <TableHead className="text-right">Action</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        <TableRow className="border-white/10 hover:bg-card2/50">
                          <TableCell>
                            <span className="flex h-6 w-6 items-center justify-center rounded-full bg-red-600/20">
                              <X className="h-4 w-4 text-red-600" />
                            </span>
                          </TableCell>
                          <TableCell className="font-medium">ALT-2025-03-27-001</TableCell>
                          <TableCell>Kiwi Retail Group</TableCell>
                          <TableCell>Lambton Quay Store</TableCell>
                          <TableCell>Motion Detected</TableCell>
                          <TableCell>Today, 10:23 AM</TableCell>
                          <TableCell className="text-right">
                            <Button variant="ghost" size="sm">
                              View
                            </Button>
                          </TableCell>
                        </TableRow>
                        <TableRow className="border-white/10 hover:bg-card2/50">
                          <TableCell>
                            <span className="flex h-6 w-6 items-center justify-center rounded-full bg-green-600/20">
                              <Check className="h-4 w-4 text-green-600" />
                            </span>
                          </TableCell>
                          <TableCell className="font-medium">ALT-2025-03-26-042</TableCell>
                          <TableCell>Wellington Security Ltd</TableCell>
                          <TableCell>Head Office</TableCell>
                          <TableCell>Door Open</TableCell>
                          <TableCell>Yesterday, 8:45 PM</TableCell>
                          <TableCell className="text-right">
                            <Button variant="ghost" size="sm">
                              View
                            </Button>
                          </TableCell>
                        </TableRow>
                        <TableRow className="border-white/10 hover:bg-card2/50">
                          <TableCell>
                            <span className="flex h-6 w-6 items-center justify-center rounded-full bg-yellow-600/20">
                              <AlertTriangle className="h-4 w-4 text-yellow-600" />
                            </span>
                          </TableCell>
                          <TableCell className="font-medium">ALT-2025-03-25-018</TableCell>
                          <TableCell>Auckland Properties Group</TableCell>
                          <TableCell>Newmarket Building</TableCell>
                          <TableCell>System Offline</TableCell>
                          <TableCell>2 days ago, 3:12 PM</TableCell>
                          <TableCell className="text-right">
                            <Button variant="ghost" size="sm">
                              View
                            </Button>
                          </TableCell>
                        </TableRow>
                        <TableRow className="border-white/10 hover:bg-card2/50">
                          <TableCell>
                            <span className="flex h-6 w-6 items-center justify-center rounded-full bg-red-600/20">
                              <X className="h-4 w-4 text-red-600" />
                            </span>
                          </TableCell>
                          <TableCell className="font-medium">ALT-2025-03-24-099</TableCell>
                          <TableCell>Kiwi Retail Group</TableCell>
                          <TableCell>Queenstown Store</TableCell>
                          <TableCell>Motion Detected</TableCell>
                          <TableCell>3 days ago, 11:05 PM</TableCell>
                          <TableCell className="text-right">
                            <Button variant="ghost" size="sm">
                              View
                            </Button>
                          </TableCell>
                        </TableRow>
                        <TableRow className="border-white/10 hover:bg-card2/50">
                          <TableCell>
                            <span className="flex h-6 w-6 items-center justify-center rounded-full bg-green-600/20">
                              <Check className="h-4 w-4 text-green-600" />
                            </span>
                          </TableCell>
                          <TableCell className="font-medium">ALT-2025-03-23-027</TableCell>
                          <TableCell>Wellington Security Ltd</TableCell>
                          <TableCell>Warehouse</TableCell>
                          <TableCell>Door Open</TableCell>
                          <TableCell>4 days ago, 7:30 AM</TableCell>
                          <TableCell className="text-right">
                            <Button variant="ghost" size="sm">
                              View
                            </Button>
                          </TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                    <div className="mt-4 flex items-center justify-between">
                      <div className="text-sm text-white/70">Showing 5 of 42 alerts</div>
                      <div className="flex items-center gap-2">
                        <Button variant="outline" size="sm" disabled>
                          Previous
                        </Button>
                        <Button variant="outline" size="sm">
                          Next
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </TabsContent>

            <TabsContent value="activity">
              <Card className="border-white/10">
                <CardHeader>
                  <CardTitle className="text-white">Recent Activity</CardTitle>
                  <CardDescription>User's recent system activity</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    {existingUser ? (
                      <>
                        <div className="border-b border-white/10 pb-2">
                          <p className="text-sm text-white">
                            Last login: {existingUser.lastActive}
                          </p>
                          <p className="text-xs text-gray-400">IP: 192.168.1.1</p>
                        </div>
                        <div className="space-y-2">
                          <p className="text-sm text-white">Recent actions:</p>
                          <ul className="space-y-1 text-sm text-gray-400">
                            <li>• Updated profile information (2 days ago)</li>
                            <li>• Changed permissions for Site A (1 week ago)</li>
                            <li>• Accessed analytics dashboard (2 weeks ago)</li>
                          </ul>
                        </div>
                      </>
                    ) : (
                      <p className="text-sm text-gray-400">
                        No activity data available for new users
                      </p>
                    )}
                  </div>
                </CardContent>
              </Card>
            </TabsContent>
          </div>
        </Tabs>
      </div>
    </>
  );
}
